import React from 'react';

import { Box } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'gatsby-plugin-react-i18next';

import { DepartureDate, ReturnDate } from '../../../domain/travels';
import { FieldError } from 'react-hook-form';

// ********************************************************************************
interface Props {
  departureDate?: DepartureDate | null;
  returnDate?: ReturnDate | null;

  /** isReturnDateDisabled will be true when the Travel Type is Round Trip */
  isReturnDateDisabled?: boolean;
  /** Departure date and Return date are using the first part of ISO String date */
  onDateChange: (departureDate: DepartureDate | null, returnDate: ReturnDate | null) => void;
  departureDateError?: string | null | { message: string };
  returnDateError?: string | null | { message: string };
}

export const TravelDateSelect = ({ isReturnDateDisabled = false, departureDate, returnDate, onDateChange, departureDateError, returnDateError }: Props) => {
  const { t } = useTranslation();
  const today = dayjs();

  // == Handlers ==================================================================
  const handleDepartureDateChange = (newDate: Dayjs | null) => {
    if (!newDate || !newDate.isValid()) return;

    const isoStringDate = newDate.toISOString();
    const newDepartureDate = isoStringDate.split('T')[0] as DepartureDate;
    
    // If the return date is before the departure date, reset the return date
    if (returnDate && dayjs(returnDate).isBefore(newDate)) {
      onDateChange(newDepartureDate, null);
      return;
    }

    onDateChange(newDepartureDate, returnDate ?? null);
  };

  const handleReturnDateChange = (newDate: Dayjs | null) => {
    if (!newDate || !newDate.isValid()) return;

    const isoStringDate = newDate.toISOString();
    const newReturnDate = isoStringDate.split('T')[0] as ReturnDate;
    onDateChange(departureDate ?? null, newReturnDate);
  };

  // == Render ====================================================================
  return (
    <Box gap={3} display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}>
      <DatePicker
        value={departureDate ? dayjs(departureDate) : null}
        label={t('inputs.departure_date')}
        minDate={today}
        disablePast
        onChange={handleDepartureDateChange}
        slotProps={{
          textField: {
              fullWidth: true,
              variant: 'outlined',
              error: !!departureDateError,
              helperText: departureDateError?.message ? departureDateError?.message : '',
          },
      }}
      />

      <DatePicker
        value={returnDate ? dayjs(returnDate) : null}
        label={t('inputs.return_date')}
        minDate={departureDate ? dayjs(departureDate) : today}
        disabled={isReturnDateDisabled}
        disablePast
        onChange={handleReturnDateChange}
        slotProps={{
          textField: {
              fullWidth: true,
              variant: 'outlined',
              error: !!returnDateError,
              helperText: returnDateError?.message ? returnDateError?.message : '',
          },
      }}
      />
    </Box>
  );
};
